import React, { useEffect, useState } from "react";
import axios from "axios";
import "./PreviewBatchStudents.css";
import Constants from "variables/Constants";
import { useLocation, useParams } from "react-router-dom";
import Button from "components/CustomButtons/Button.js";
// Brand art for the header (teacher holding report card) and the sessions avatar.
import teacherPlaceholder from "assets/img/report-teacher.png";
import avatarPlaceholder from "assets/img/report-avatar.png";

function OrdinalDate({ dateStr }) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const day = d.getDate();
    const mod100 = day % 100;
    let suffix;
    if (mod100 === 11 || mod100 === 12 || mod100 === 13) {
      suffix = "th";
    } else {
      suffix = ["th", "st", "nd", "rd", "th", "th", "th", "th", "th", "th"][
        day % 10
      ];
    }
    const month = d.toLocaleString("en-US", { month: "long" });
    const year = d.getFullYear();
    return (
      <>
        {day}
        <sup>{suffix}</sup> {month}, {year}
      </>
    );
  } catch {
    return null;
  }
}

function DateRangeLabel({ startDate, endDate }) {
  if (!startDate) return <>All Sessions</>;
  if (endDate) {
    return (
      <>
        <OrdinalDate dateStr={startDate} />
        {" – "}
        <OrdinalDate dateStr={endDate} />
      </>
    );
  }
  return <OrdinalDate dateStr={startDate} />;
}

function BarChartIcon({ small = false }) {
  return (
    <span className={`barchart-icon${small ? " small" : ""}`}>
      <span className="b1" />
      <span className="b2" />
      <span className="b3" />
      <span className="b4" />
    </span>
  );
}

function Badge({ percentage }) {
  if (percentage >= 80) {
    return (
      <span className="badge ontrack">
        <span className="badge-icon">✓</span>
        <span className="badge-text">On Track</span>
      </span>
    );
  }
  if (percentage >= 50) {
    return (
      <span className="badge imprv">
        <span className="badge-icon">+</span>
        <span className="badge-text">Need Imprv.</span>
      </span>
    );
  }
  return (
    <span className="badge critical">
      <span className="badge-icon">−</span>
      <span className="badge-text">Critical</span>
    </span>
  );
}

export default function PreviewBatchStudents({
  sessionsConducted = 0,
  totalSessions = 0,
  progressPercentage = 0,

  teacherImg,
  avatarImg,
}) {
  const { id } = useParams();
  const location = useLocation();
  const [previewStudentData, setPreviewStudentData] = useState({});
  const [students, setStudents] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  // Drives the .loading-screen marker the PDF service waits to disappear, so
  // the report is only captured AFTER all student rows have rendered.
  const [loading, setLoading] = useState(true);

  // When opened via the standalone "/previewBatchStudents/:id" route (used by
  // the PDF service), render only the report — no Download button, no chrome.
  const isPrintView = window.location.pathname.startsWith(
    "/previewBatchStudents",
  );

  const getPreviewStudents = async () => {
    try {
      // Filters the list page passed through the URL
      // (search/user_type/attempt_id/status/performance).
      const filterQuery = (location.search || "").replace(/^\?/, "");

      // The /batch-students/preview endpoint carries the report HEADER meta
      // (batch name, date range, sessions conducted, progress) but it IGNORES
      // the list filters — it always returns the full batch. So we use it only
      // for the header, and pull the actual (filtered) student rows from the
      // SAME endpoint the list screen uses, which DOES honor every filter.
      const metaUrl =
        Constants.getUrls.getPreviewStudents + `?ftof_batch_id=${id}`;

      // per_page is forced high so the PDF reflects the FULL filtered set, not
      // just one page (the list page intentionally drops pagination for PDFs).
      const studentsUrl =
        Constants.getUrls.getFaceBatchStudents +
        `?ftof_batch_id=${id}` +
        (filterQuery ? `&${filterQuery}` : "") +
        `&per_page=100000`;

      // This standalone print route renders outside the Admin layout, so the
      // global WindowToken axios header is never set. Attach the token the PDF
      // service seeded into localStorage so these calls stay authorized if the
      // report APIs ever require it (they're public today).
      const authToken = localStorage.getItem("accessToken");
      const config = authToken
        ? { headers: { Authorization: `Bearer ${authToken}` } }
        : {};

      const [metaRes, studentsRes] = await Promise.all([
        axios.get(metaUrl, config),
        axios.get(studentsUrl, config),
      ]);

      // Header meta from the preview endpoint.
      setPreviewStudentData(metaRes.data.data || {});

      // The list endpoint returns a Laravel paginator: the rows live in `.data`.
      const rows = studentsRes.data && studentsRes.data.data;
      setStudents(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.error("Error fetching preview students:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPreviewStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  const progressPct = Math.max(
    0,
    Math.min(100, Math.floor(previewStudentData.progress_percentage)),
  );

  // If the list page passed a Regenerate date range via the URL, show THAT
  // period in the report header (the report was scoped to it). Otherwise fall
  // back to the batch's stored date range from the preview endpoint.
  const urlParams = new URLSearchParams(location.search || "");
  const headerStartDate =
    urlParams.get("start_date") || previewStudentData.start_date;
  const headerEndDate =
    urlParams.get("end_date") || previewStudentData.end_date;

  // Direct, editable (vector text), pixel-match PDF via the pdf-gen service.
  // The service renders THIS page in a headless Chrome and returns a real PDF
  // (selectable/editable text, exact UI) which we download straight to disk —
  // no browser print dialog. Locally it talks to the dev server on :5000;
  // anywhere else it uses the deployed service.
  const PDF_SERVICE =
    window.location.hostname === "localhost"
      ? "http://localhost:5000"
      : "https://pdfgen.akclasses.in";

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const docName = `${previewStudentData.batch_name ||
        "Batch"} Attendance Report.pdf`;
      // Render the chrome-free standalone print route (no auth gate / redirects).
      // Preserve the current URL query (filters + any Regenerate date range) so
      // the generated PDF matches exactly what's shown on this preview page.
      const printPageUrl =
        `${window.location.origin}/previewBatchStudents/${id}` +
        (location.search || "");
      const clientUrl = encodeURIComponent(printPageUrl);
      // Pass the admin auth token so the headless browser can seed it into the
      // preview page's localStorage — keeps the PDF working if the report APIs
      // ever start requiring auth (they're public today).
      const authToken = localStorage.getItem("accessToken");
      const pdfUrl =
        `${PDF_SERVICE}/generate-pdf?clientUrl=${clientUrl}` +
        `&appear=${encodeURIComponent("#ak-report-container")}` +
        `&gone=${encodeURIComponent(
          ".loading-screen,.image-loading-placeholder",
        )}` +
        `&format=A4&orientation=portrait` +
        `&name=${encodeURIComponent(docName)}&compression=false` +
        (authToken ? `&token=${encodeURIComponent(authToken)}` : "");

      const res = await fetch(pdfUrl);
      if (!res.ok) {
        const errText = await res.text();
        console.error("PDF service error:", res.status, errText);
        alert(`Failed to generate PDF (${res.status}). Please try again.`);
        return;
      }

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = docName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 2000);
    } catch (error) {
      console.error("PDF download error:", error);
      alert("Failed to generate PDF, please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // ---- A4 PAGINATION ------------------------------------------------------
  // Split the student list into explicit A4 "sheets" so each maps to exactly
  // one PDF page: page 1 carries the full branded header, later pages repeat
  // only the attendance column headers. Rows are forced to a single line (see
  // .student-name in the CSS) so every data row is the same height and these
  // counts stay accurate. Tune them if you change the row font/padding.
  // Measured against the real A4 render: a first page fits ~16 rows (header
  // eats the rest) and a continuation page ~23. We sit one row under each so a
  // sheet always fills exactly one page and never spills a few rows onto the
  // next (which is what caused the broken breaks).
  const FIRST_PAGE_ROWS = 15; // page 1 carries the full branded header
  const OTHER_PAGE_ROWS = 21; // continuation pages: equal 14pt card padding all
  // sides eats ~half a row vs before, so 21 keeps a safe buffer (no spill).

  const studentPages = [];
  {
    let cursor = 0;
    let pageSize = FIRST_PAGE_ROWS;
    do {
      studentPages.push({
        startIndex: cursor,
        rows: students.slice(cursor, cursor + pageSize),
      });
      cursor += pageSize;
      pageSize = OTHER_PAGE_ROWS;
    } while (cursor < students.length);
  }

  const renderStudentRow = (student, index) => {
    const present = parseInt(student.present_value ?? student.present ?? 0, 10);
    const absent = parseInt(student.absent_value ?? student.absent ?? 0, 10);
    let percentage;
    if (student.total_average != null) {
      percentage = parseFloat(student.total_average);
    } else {
      const total = Math.max(1, present + absent);
      percentage = (present / total) * 100;
    }
    const rowClass = index % 2 === 0 ? "odd" : "even";
    return (
      <tr key={index} className={rowClass}>
        <td className="sno">{index + 1}</td>
        <td className="student-name">{student.name}</td>
        <td>{present}</td>
        <td>{absent}</td>
        <td className="pct">{Math.round(percentage)}%</td>
        <td>
          <Badge percentage={percentage} />
        </td>
      </tr>
    );
  };

  const AttendanceHead = () => (
    <thead>
      <tr>
        <th style={{ width: "10%" }}>S.No.</th>
        <th style={{ width: "30%" }}>Student Name</th>
        <th style={{ width: "12%" }}>Present</th>
        <th style={{ width: "12%" }}>Absent</th>
        <th style={{ width: "20%" }}>Attendance %</th>
        <th style={{ width: "20%" }}>Status</th>
      </tr>
    </thead>
  );

  return (
    <div>
      {loading && (
        <div
          className="loading-screen"
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#ede4fa",
            zIndex: 9999,
            fontFamily: "Arial, sans-serif",
            color: "#4a24a8",
          }}
        >
          Loading report…
        </div>
      )}
      {!isPrintView && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={handleDownload}
            disabled={isDownloading}
            style={{
              backgroundColor: "#4a24a8",
              opacity: isDownloading ? 0.7 : 1,
            }}
          >
            {isDownloading ? "Generating..." : "Download"}
          </Button>
        </div>
      )}
      <div id="ak-report-container" className="preview-root">
        {studentPages.map((pageData, pageIndex) => {
          const isFirst = pageIndex === 0;
          return (
            <div className="sheet" key={pageIndex}>
              <div className={isFirst ? "page" : "page cont"}>
                {isFirst && (
                  <>
                    {/* HEADER + DATE BLOCK */}
                    <div className="header-wrap">
                      <div className="header">
                        <table className="header-title-table">
                          <tbody>
                            <tr>
                              <td className="icon-cell">
                                <BarChartIcon />
                              </td>
                              <td>
                                <h1>LIVE BATCH ATTENDANCE REPORT</h1>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <img
                        className="header-person"
                        src={teacherImg || teacherPlaceholder}
                        alt=""
                      />
                    </div>
                    <div className="date-block">
                      <span className="date-pill">
                        <span className="cal-icon">
                          <span className="cal-strip" />
                        </span>
                        <DateRangeLabel
                          startDate={headerStartDate}
                          endDate={headerEndDate}
                        />
                      </span>
                    </div>

                    {/* SESSIONS ROW */}
                    <table className="sessions-table">
                      <tbody>
                        <tr>
                          <td className="avatar-cell">
                            <span className="avatar-ring">
                              <img
                                className="sessions-avatar"
                                src={avatarImg || avatarPlaceholder}
                                alt=""
                              />
                            </span>
                          </td>
                          <td>
                            <div className="sessions-pill">
                              <div className="label">
                                Sessions Conducted :{" "}
                                <span className="count">
                                  {previewStudentData.sessions_conducted}/
                                  {previewStudentData.total_sessions}
                                </span>
                              </div>
                              <div className="progress-outer">
                                <div className="progress-wrap">
                                  <div
                                    className="progress-fill"
                                    style={{ width: `${progressPct}%` }}
                                  />
                                </div>
                                <span className="progress-label">
                                  {progressPct}%
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </>
                )}

                {/* REPORT SECTION */}
                <div className="report-section">
                  {isFirst && (
                    <>
                      {/* LEGEND */}
                      <div className="legend-table">
                        <span className="legend-item">
                          <span className="legend-icon green">✓</span>
                          80%+ : Excellent / Exam Ready
                        </span>
                        <span className="sep">|</span>
                        <span className="legend-item">
                          <span className="legend-icon amber">+</span>
                          50%- 79% : Improvement Needed
                        </span>
                        <span className="sep">|</span>
                        <span className="legend-item">
                          <span className="legend-icon red">−</span>
                          Below 50%
                        </span>
                      </div>

                      {/* SECTION TITLE */}
                      <div className="section-title">
                        <BarChartIcon small /> Student-wise Attendance Report
                      </div>
                    </>
                  )}

                  {/* ATTENDANCE TABLE */}
                  <table className={isFirst ? "attendance" : "attendance cont"}>
                    <AttendanceHead />
                    <tbody>
                      {pageData.rows.map((student, i) =>
                        renderStudentRow(student, pageData.startIndex + i),
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
